<template>
    <div class="contacto-page">
        <!-- Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span>
                    <span class="current">Contacto</span>
                </nav>
                <h1 class="page-title">Contáctanos</h1>
                <p class="page-subtitle">
                    Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos pronto.
                </p>
            </div>
        </section>
        
        <div class="container">
            <div class="contact-layout">
                <!-- Contact Form -->
                <div class="contact-form-section">
                    <form @submit.prevent="submitForm" class="contact-form">
                        <div class="form-row">
                            <div class="form-group">
                                <label for="nombre">Nombre Completo *</label>
                                <input 
                                    type="text" 
                                    id="nombre"
                                    v-model="form.nombre"
                                    required
                                    placeholder="Tu nombre"
                                >
                            </div>
                            
                            <div class="form-group">
                                <label for="empresa">Empresa</label>
                                <input 
                                    type="text" 
                                    id="empresa"
                                    v-model="form.empresa"
                                    placeholder="Nombre de tu empresa"
                                >
                            </div>
                        </div>
                        
                        <div class="form-row">
                            <div class="form-group">
                                <label for="email">Email *</label>
                                <input 
                                    type="email" 
                                    id="email"
                                    v-model="form.email"
                                    required
                                    placeholder="tu@email.com"
                                >
                            </div>
                            
                            <div class="form-group">
                                <label for="telefono">Teléfono *</label>
                                <input 
                                    type="tel" 
                                    id="telefono"
                                    v-model="form.telefono"
                                    required
                                    placeholder="+51 999 999 999"
                                >
                            </div>
                        </div>
                        
                        <div class="form-group">
                            <label for="asunto">Asunto *</label>
                            <input 
                                type="text" 
                                id="asunto"
                                v-model="form.asunto"
                                required
                                placeholder="¿En qué podemos ayudarte?"
                            >
                        </div>
                        
                        <div class="form-group">
                            <label for="mensaje">Mensaje *</label>
                            <textarea 
                                id="mensaje"
                                v-model="form.mensaje"
                                required
                                rows="6"
                                placeholder="Escribe tu mensaje aquí..."
                            ></textarea>
                        </div>
                        
                        <button type="submit" class="btn btn-primary" :disabled="sending">
                            <span v-if="!sending">
                                <i class="fas fa-paper-plane"></i>
                                Enviar Mensaje
                            </span>
                            <span v-else>
                                <i class="fas fa-spinner fa-spin"></i>
                                Enviando...
                            </span>
                        </button>
                        
                        <transition name="fade">
                            <div v-if="successMessage" class="alert alert-success">
                                <i class="fas fa-check-circle"></i>
                                {{ successMessage }}
                            </div>
                        </transition>
                        
                        <transition name="fade">
                            <div v-if="errorMessage" class="alert alert-error">
                                <i class="fas fa-exclamation-circle"></i>
                                {{ errorMessage }}
                            </div>
                        </transition>
                    </form>
                </div>
                
                <!-- Contact Info -->
                <div class="contact-info-section">
                    <div class="info-card">
                        <h3>Información de Contacto</h3>
                        
                        <div class="info-item" v-if="config.address">
                            <div class="info-icon">
                                <i class="fas fa-map-marker-alt"></i>
                            </div>
                            <div class="info-content">
                                <h4>Dirección</h4>
                                <p>{{ config.address }}</p>
                                <p>{{ config.district }}, {{ config.city }}, {{ config.country }}</p>
                            </div>
                        </div>
                        
                        <div class="info-item" v-if="phones.length">
                            <div class="info-icon">
                                <i class="fas fa-phone"></i>
                            </div>
                            <div class="info-content">
                                <h4>Teléfonos</h4>
                                <a 
                                    v-for="phone in phones" 
                                    :key="phone.id"
                                    :href="`tel:${phone.phone}`"
                                    class="info-link"
                                >
                                    {{ phone.label }}: {{ phone.phone }}
                                </a>
                            </div>
                        </div>
                        
                        <div class="info-item" v-if="emails.length">
                            <div class="info-icon">
                                <i class="fas fa-envelope"></i>
                            </div>
                            <div class="info-content">
                                <h4>Email</h4>
                                <a 
                                    v-for="email in emails" 
                                    :key="email.id"
                                    :href="`mailto:${email.email}`"
                                    class="info-link"
                                >
                                    {{ email.label }}: {{ email.email }}
                                </a>
                            </div>
                        </div>
                        
                        <div class="info-item" v-if="socialNetworks.length">
                            <div class="info-icon">
                                <i class="fas fa-share-alt"></i>
                            </div>
                            <div class="info-content">
                                <h4>Redes Sociales</h4>
                                <div class="social-links">
                                    <a 
                                        v-for="social in socialNetworks" 
                                        :key="social.id"
                                        :href="social.url"
                                        target="_blank"
                                        class="social-link"
                                        :title="social.name"
                                    >
                                        <i :class="getSocialIcon(social.name)"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Map -->
                    <div class="map-card" v-if="config.latitude && config.longitude">
                        <iframe 
                            :src="`https://www.google.com/maps?q=${config.latitude},${config.longitude}&hl=es&z=15&output=embed`"
                            frameborder="0"
                            allowfullscreen
                        ></iframe>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import api from '../services/api';

export default {
    name: 'Contacto',
    data() {
        return {
            form: {
                nombre: '',
                empresa: '',
                email: '',
                telefono: '',
                asunto: '',
                mensaje: ''
            },
            sending: false,
            successMessage: '',
            errorMessage: ''
        };
    },
    computed: {
        ...mapState(['config']),
        ...mapGetters(['emails', 'phones', 'socialNetworks'])
    },
    methods: {
        async submitForm() {
            try {
                this.sending = true;
                this.successMessage = '';
                this.errorMessage = '';
                
                await api.sendContact(this.form);
                
                this.successMessage = '¡Mensaje enviado exitosamente! Te responderemos pronto.';
                this.resetForm();
            } catch (error) {
                console.error('Error sending contact:', error);
                this.errorMessage = 'Hubo un error al enviar el mensaje. Por favor, intenta nuevamente.';
            } finally {
                this.sending = false;
            }
        },
        resetForm() {
            this.form = {
                nombre: '',
                empresa: '',
                email: '',
                telefono: '',
                asunto: '',
                mensaje: ''
            };
        },
        getSocialIcon(name) {
            const icons = {
                'facebook': 'fab fa-facebook-f',
                'instagram': 'fab fa-instagram',
                'twitter': 'fab fa-twitter',
                'linkedin': 'fab fa-linkedin-in',
                'youtube': 'fab fa-youtube',
                'tiktok': 'fab fa-tiktok'
            };
            return icons[name.toLowerCase()] || 'fas fa-link';
        }
    }
};
</script>

<style scoped>
.contacto-page {
    background-color: var(--background);
    min-height: 100vh;
    padding-top: 80px;
}

.page-header {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    padding: 60px 2rem;
    position: relative;
}

.page-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.1), transparent 60%);
}

.breadcrumbs {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
}

.breadcrumbs a {
    color: var(--text-gray);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-gold);
}

.breadcrumbs .separator {
    color: rgba(212, 175, 55, 0.3);
}

.breadcrumbs .current {
    color: var(--accent-gold);
    font-weight: 600;
}

.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-light);
}

.page-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
}

.contact-layout {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 3rem;
    margin: 5rem auto;
}

.contact-form {
    background: var(--card-bg);
    padding: 2.5rem;
    border-radius: 16px;
    border: 1px solid rgba(212, 175, 55, 0.1);
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    font-weight: 600;
    color: var(--text-light);
    margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea {
    width: 100%;
    padding: 0.875rem 1rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(212, 175, 55, 0.2);
    border-radius: 8px;
    color: var(--text-light);
    font-size: 1rem;
    transition: all 0.3s ease;
    font-family: inherit;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
    color: var(--text-gray);
}

.form-group input:focus,
.form-group textarea:focus {
    outline: none;
    border-color: var(--accent-gold);
    background: rgba(255, 255, 255, 0.08);
}

.form-group textarea {
    resize: vertical;
}

.btn {
    width: 100%;
    padding: 1rem 2rem;
    border-radius: 8px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    transition: all 0.3s ease;
    font-size: 1rem;
}

.btn-primary {
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    color: var(--primary-dark);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(212, 175, 55, 0.3);
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.alert {
    margin-top: 1.5rem;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.alert-success {
    background: rgba(45, 212, 191, 0.1);
    color: var(--accent-teal);
    border: 1px solid var(--accent-teal);
}

.alert-error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid #ef4444;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Contact Info */
.contact-info-section {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.info-card,
.map-card {
    background: var(--card-bg);
    padding: 2.5rem;
    border-radius: 16px;
    border: 1px solid rgba(212, 175, 55, 0.1);
}

.info-card h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--accent-gold);
    margin-bottom: 2rem;
}

.info-item {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-dark);
    font-size: 1.25rem;
    flex-shrink: 0;
}

.info-content h4 {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 0.5rem;
}

.info-content p {
    color: var(--text-gray);
    line-height: 1.6;
}

.info-link {
    display: block;
    color: var(--accent-teal);
    text-decoration: none;
    margin-bottom: 0.25rem;
    transition: color 0.3s ease;
}

.info-link:hover {
    color: var(--accent-gold);
}

.social-links {
    display: flex;
    gap: 1rem;
    margin-top: 0.75rem;
}

.social-link {
    width: 40px;
    height: 40px;
    background: rgba(212, 175, 55, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent-gold);
    text-decoration: none;
    transition: all 0.3s ease;
}

.social-link:hover {
    background: var(--accent-gold);
    color: var(--primary-dark);
    transform: translateY(-3px);
}

.map-card {
    padding: 0;
    overflow: hidden;
    height: 300px;
}

.map-card iframe {
    width: 100%;
    height: 100%;
}

@media (max-width: 992px) {
    .contact-layout {
        grid-template-columns: 1fr;
    }
    
    .form-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .page-title {
        font-size: 2rem;
    }
}

@media (max-width: 576px) {
    .container {
        padding: 0 1rem;
    }
    
    .contact-form,
    .info-card {
        padding: 1.5rem;
    }
}
</style>